import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Radio, Code2, ShieldCheck, Check, ArrowRight } from "lucide-react";

const TABS = [
  {
    key: "radio",
    label: "Radio",
    icon: Radio,
    title: "Radio consultancy & setup",
    desc: "Expert, independent guidance for your radio & streaming apps — plus full setup across car, phone and streaming devices.",
    from: "$119.99",
    bullets: ["Independent radio & streaming app guidance", "Setup & activation on all your devices", "15-day free-fix guarantee"],
    href: "#radio",
    cta: "Explore radio",
  },
  {
    key: "dev",
    label: "Development",
    icon: Code2,
    title: "Web, app & software development",
    desc: "From websites and mobile apps to custom software and SaaS — designed, built and shipped for a one-time project charge.",
    from: "$49.99",
    bullets: ["Web & mobile app development", "Custom software & SaaS", "One-time project pricing, no subscription"],
    href: "#plans",
    cta: "Explore development",
  },
  {
    key: "antivirus",
    label: "Security & Antivirus",
    icon: ShieldCheck,
    title: "Genuine antivirus, activated for you",
    desc: "Authorized reseller of leading antivirus brands with free installation, activation and unlimited support.",
    from: "$149.99",
    bullets: ["Genuine, licensed software", "Norton, McAfee, Bitdefender, F-Secure & ESET", "Free setup & 7-day money-back"],
    href: "#antivirus",
    cta: "Explore antivirus",
  },
];

export default function ServiceTabs() {
  const [active, setActive] = useState("radio");
  const tab = TABS.find((t) => t.key === active);

  const go = (href) => document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });

  return (
    <section id="services-tabs" className="bg-[#1B2C4E] text-white border-y border-white/10" data-testid="service-tabs-section">
      <div className="max-w-6xl mx-auto px-6 py-24">
        <div className="text-center max-w-2xl mx-auto">
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#60A5FA]">Everything we do</p>
          <h2 className="font-display text-3xl sm:text-4xl tracking-tight uppercase mt-3">Explore our services</h2>
          <p className="text-white/60 mt-4">Three ways we help — pick a category to see what's included.</p>
        </div>

        {/* Tab pills */}
        <div className="mt-10 flex flex-wrap justify-center gap-2" role="tablist">
          {TABS.map((t) => (
            <button
              key={t.key}
              role="tab"
              aria-selected={active === t.key}
              onClick={() => setActive(t.key)}
              className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition-all duration-300 ${
                active === t.key ? "bg-[#3B82F6] text-white shadow-lg" : "bg-white/[0.06] text-white/70 hover:bg-white/[0.12] hover:text-white"
              }`}
              data-testid={`service-tab-${t.key}`}
            >
              <t.icon className="h-4 w-4" /> {t.label}
            </button>
          ))}
        </div>

        {/* Tab panel */}
        <AnimatePresence mode="wait">
          <motion.div
            key={tab.key}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.3 }}
            className="mt-10 rounded-3xl bg-white/[0.04] border border-white/10 p-8 sm:p-10"
            data-testid={`service-panel-${tab.key}`}
          >
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
              <div>
                <div className="inline-flex h-12 w-12 rounded-2xl bg-[#3B82F6]/15 items-center justify-center">
                  <tab.icon className="h-6 w-6 text-[#60A5FA]" />
                </div>
                <h3 className="font-display text-2xl sm:text-3xl tracking-tight uppercase mt-5">{tab.title}</h3>
                <p className="text-white/65 mt-4 leading-relaxed">{tab.desc}</p>
                <button
                  onClick={() => go(tab.href)}
                  className="btn-sweep mt-7 inline-flex items-center gap-2 bg-[#3B82F6] text-white font-bold px-7 py-3 rounded-full hover:bg-[#2563EB] transition-colors duration-300"
                  data-testid={`service-cta-${tab.key}`}
                >
                  {tab.cta} <ArrowRight className="h-4 w-4" />
                </button>
              </div>
              <div className="rounded-2xl bg-[#1B2C4E] border border-white/10 p-7">
                <p className="text-xs uppercase tracking-[0.2em] text-white/50">Starting at</p>
                <p className="font-display text-4xl font-bold text-[#60A5FA] mt-1">{tab.from}</p>
                <p className="text-xs text-white/50 mt-1">one-time</p>
                <ul className="mt-6 space-y-3">
                  {tab.bullets.map((b) => (
                    <li key={b} className="flex items-start gap-3 text-sm text-white/85">
                      <Check className="h-4 w-4 text-[#60A5FA] shrink-0 mt-0.5" /> <span>{b}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
}
